'use client';
// GED — application mono-page : le Shell gère session + navigation, chaque
// écran est monté selon la vue courante du store.
import React from 'react';
import { useGed } from '@/lib/store';
import { Shell } from '@/components/Shell';
import { Dashboard } from '@/components/screens/Dashboard';
import { Documents } from '@/components/screens/Documents';
import { DocDetail, Review } from '@/components/screens/DocDetail';
import { Demandes } from '@/components/screens/Demandes';
import { Diffusion } from '@/components/screens/Diffusion';
import { Retrait } from '@/components/screens/Retrait';
import { NC } from '@/components/screens/NC';
import { Actions } from '@/components/screens/Actions';
import { Reclamations } from '@/components/screens/Reclamations';
import { Audits } from '@/components/screens/Audits';
import { Rdd } from '@/components/screens/Rdd';
import { Risques } from '@/components/screens/Risques';
import { Objectifs } from '@/components/screens/Objectifs';
import { Kpi } from '@/components/screens/Kpi';
import { Situations } from '@/components/screens/Situations';
import { RevueRef } from '@/components/screens/RevueRef';
import { Criteres } from '@/components/screens/Criteres';
import { Continuite } from '@/components/screens/Continuite';
import { Suggestions } from '@/components/screens/Suggestions';
import { Retours } from '@/components/screens/Retours';
import { DemandesRdv } from '@/components/screens/DemandesRdv';
import { AutoEvaluation } from '@/components/screens/AutoEvaluation';
import { Materiel } from '@/components/screens/Materiel';
import { Produits } from '@/components/screens/Produits';
import { RevueContrats } from '@/components/screens/RevueContrats';
import { Annuaire } from '@/components/screens/annuaire/Annuaire';
import { EcranConventions } from '@/components/screens/annuaire/Conventions';
import { Calibrations } from '@/components/screens/ressources/Calibrations';
import { Metrologie } from '@/components/screens/ressources/Metrologie';
import { Maintenances } from '@/components/screens/ressources/Maintenances';
import { RelevesTemperature } from '@/components/screens/ressources/RelevesTemperature';
import { Equipements, Cqi, Eeq, Organigramme, Habilitations, Processus, VueAuditeur , CqiSaisie, CqeEnvois, QualiteAnalytique } from '@/components/screens/Ressources';
import { Eil } from '@/components/screens/Eil';
import { Referentiel } from '@/components/screens/Referentiel';
import { Dvm } from '@/components/screens/Dvm';
import { Incertitude } from '@/components/screens/ressources/Incertitude';
import { Iso } from '@/components/screens/Iso';
import { Coherence } from '@/components/screens/Coherence';
import { Notifications } from '@/components/screens/Notifications';
import { Planning, Postes } from '@/components/screens/Rh';
import ReglesAffectation from '@/components/screens/ReglesAffectation';
import Calendrier from '@/components/screens/Calendrier';
import { FichePoste } from '@/components/screens/RhFichePoste';
import Checklists from '@/components/screens/Checklists';
import { MesConges, Absences } from '@/components/screens/RhAbsences';
import { Presences } from '@/components/screens/RhPresences';
import { Paie } from '@/components/screens/RhPaie';
import { RhHabilitationsEcran } from '@/components/screens/RhHabilitations';
import { MonDossier } from '@/components/screens/RhMonDossier';
import { DossiersRh } from '@/components/screens/DossiersRh';
import { MonPlanning } from '@/components/screens/MonPlanning';
import { MesPresences } from '@/components/screens/MesPresences';
import { MonParcours } from '@/components/screens/MonParcours';

/** Le parcours de la personne choisie (`selectedPersonne`) ; sans personne, le sien. */
function ParcoursPersonne() {
  const orgId = useGed((s) => s.selectedPersonne);
  return <MonParcours orgId={orgId} />;
}
import { RhJournal } from '@/components/screens/RhJournal';
import { ManuelReferentiel } from '@/components/screens/manuel/Referentiel';
import { Moment } from '@/components/screens/Moment';
import { MomentDossiers } from '@/components/screens/MomentDossiers';

/**
 * L'ancienne adresse `/analytes` mène désormais à l'onglet « Paramètres » du
 * référentiel.
 *
 * ⚠️ POURQUOI UNE REDIRECTION PLUTÔT QU'UNE SUPPRESSION SÈCHE. Le slug est un
 * CONTRAT : il a été partagé par lien, mis en signet, cité dans des comptes
 * rendus. Une page blanche à cette adresse se lit comme une panne, et c'est un
 * appel téléphonique — pas une information.
 *
 * ⚠️ `replace` et non `push` : « Précédent » doit ramener d'où l'on vient, pas
 * rejouer la redirection en boucle.
 */
function RedirigeVersParametres() {
  React.useEffect(() => {
    window.location.replace('/manuel-prelevement?onglet=parametres');
  }, []);
  return (
    <div style={{ padding: 24, fontSize: 13, color: 'var(--text-muted)' }}>
      Les paramètres et leurs valeurs usuelles se tiennent désormais au référentiel des
      analyses. Redirection…
    </div>
  );
}

function OuvreParametrage({ onglet }: { onglet: string }) {
  React.useEffect(() => {
    const etat = useGed.getState();
    etat.openSettings(onglet);
    etat.naviguerVers('dash');
  }, [onglet]);
  return null;
}

function Current() {
  const view = useGed((s) => s.view);
  switch (view) {
    case 'dash': return <Dashboard />;
    case 'list': return <Documents />;
    case 'doc': return <DocDetail />;
    case 'review': return <Review />;
    case 'req': return <Demandes />;
    case 'diff': return <Diffusion />;
    case 'retrait': return <Retrait />;
    case 'nc': return <NC />;
    /* Les statistiques des NC, avec l'envoi par correspondant (20/10/2026) : un onglet des NC, à sa propre adresse. */
    case 'statsnc': return <NC key="stats" vue="stats" />;
    case 'actions': return <Actions />;
    case 'recl': return <Reclamations />;
    case 'audits': return <Audits />;
    case 'rdd': return <Rdd />;
    case 'risques': return <Risques />;
    case 'objectifs': return <Objectifs />;
    case 'kpi': return <Kpi />;
    case 'situations': return <Situations />;
    case 'revueref': return <RevueRef />;
    case 'criteres': return <Criteres />;
    case 'continuite': return <Continuite />;
    case 'suggestions': return <Suggestions />;
    case 'retours': return <Retours />;
    case 'rdv': return <DemandesRdv />;
    case 'autoeval': return <AutoEvaluation />;
    case 'equip': return <Equipements />;
    /* Matériel (M1, 10/10/2026) : une enveloppe, trois anciennes adresses. */
    case 'fourn': return <Materiel vue="stock" />;
    case 'produits': return <Produits />;
    /* Les laboratoires sous-traitants vivent au carnet de contacts (L3, 10/10/2026) :
       l'ancienne adresse ouvre l'onglet Laboratoires. */
    /* Le carnet de contacts est SUPPRIMÉ (20/10/2026) : ses anciennes adresses ouvrent l'annuaire. */
    case 'soustraitants':
    case 'correspondants':
    case 'annuaire': return <Annuaire />;
    case 'conventions': return <EcranConventions />;
    case 'revuetiers': return <RevueContrats />;
    case 'stock': return <Materiel vue="logistique" />;
    case 'cqtdb': return <QualiteAnalytique />;
    case 'cqi': return <Cqi />;
    case 'cqisaisie': return <CqiSaisie />;
    case 'cqeenvois': return <CqeEnvois />;
    case 'eeq': return <Eeq />;
    case 'eil': return <Eil />;
    case 'cal': return <Calibrations />;
    case 'mat': return <Materiel vue="fractionnement" />;
    /* ⚠️ FERMÉ LE 30/08/2026 — « Paramètres & valeurs usuelles » était une
       SECONDE PORTE sur le même type de contenu que le référentiel
       (`ged-analyte`, 165 paramètres) : il en écrivait onze champs sans version
       et hors du circuit éditorial, sous la section `analytes` et non `manuel`.
       Ses trois colonnes propres — valeurs usuelles, revue, code équipement —
       étaient vides sur les 165 ; les bornes vivent dans `intervalle-reference`
       depuis le 25/08. Ses deux rubriques utiles (cartes CQI et critères
       d'acceptation) et la déclaration de revue ont été PORTÉES sur la fiche
       paramètre du référentiel.
       ⚠️ L'ADRESSE EST CONSERVÉE ET REDIRIGE : un slug publié est un contrat, et
       un signet qui tombe en page blanche se lit comme une panne. */
    case 'analytes': return <RedirigeVersParametres />;
    case 'metro': return <Metrologie />;
    case 'maintenances': return <Maintenances />;
    case 'relevetemp': return <RelevesTemperature />;
    case 'refperf': return <Referentiel />;
    case 'dvm': return <Dvm />;
    case 'incertitude': return <Incertitude />;
    case 'org': return <Organigramme />;
    case 'hab': return <Habilitations />;
    case 'rhhab': return <RhHabilitationsEcran />;
    case 'proc': return <Processus />;
    case 'auditview': return <VueAuditeur />;
    case 'iso': return <Iso />;
    case 'coherence': return <Coherence />;
    case 'notifs': return <Notifications />;
    case 'utilisateurs': return <OuvreParametrage onglet="utilisateurs" />;
    case 'planning': return <Planning />;
    case 'reglesplanning': return <ReglesAffectation />;
    case 'calendrier': return <Calendrier />;
    case 'postes': return <Postes />;
    case 'fichePoste': return <FichePoste />;
    case 'checklists': return <Checklists />;
    case 'mesconges': return <MesConges />;
    case 'mondossier': return <MonDossier />;
    case 'dossiers': return <DossiersRh />;
    case 'monplanning': return <MonPlanning />;
    case 'mespresences': return <MesPresences />;
    // Lot 5 (18/10/2026) : « Mes habilitations » est fondu dans « Mon parcours » ; l'adresse reste servie.
    case 'meshabilitations': return <MonParcours />;
    case 'monparcours': return <MonParcours />;
    case 'parcours': return <ParcoursPersonne />;
    case 'absences': return <Absences />;
    case 'presences': return <Presences />;
    case 'paie': return <Paie />;
    case 'rhjournal': return <RhJournal />;
    case 'manuel': return <ManuelReferentiel />;
    case 'moment': return <Moment />;
    case 'momentdossiers': return <MomentDossiers />;
    default: return <Dashboard />;
  }
}

export default function GedApp() {
  return (
    <Shell>
      <Current />
    </Shell>
  );
}
